import { useEffect, useRef } from 'react';
import { addDays } from 'date-fns';
import { getStartOfToday } from 'modules/core/utils';
import { SetFieldValue } from 'modules/core/types/form';

import { LOAN_VARIABLES_FIELDS_NAMES } from '../../consts/fieldNames.const';
import { getFirstPaymentDate } from '../BalloonPayment.service';

export interface IFirstPaymentDaysRange {
  default: number;
  min: number;
  max: number;
}
export const useRequestBalloonFirstPaymentDays = (
  balloonToggle: boolean,
  afgDealerId: string | null,
  afgLenderId: string | null,
  contractDate: Date | null,
  setValue: SetFieldValue,
  overrideFormHistoryValues: (values: Record<string, unknown>) => void,
  setFirstPaymentDaysRange: (range: IFirstPaymentDaysRange) => void,
) => {
  const requestedKeyRef = useRef<string | null>(null);
  const contractDateRef = useRef(contractDate);

  contractDateRef.current = contractDate;

  useEffect(() => {
    if (!balloonToggle || !afgDealerId || !afgLenderId) {
      requestedKeyRef.current = null;

      return;
    }

    const requestKey = `${afgDealerId}-${afgLenderId}`;

    if (requestedKeyRef.current === requestKey) return;

    requestedKeyRef.current = requestKey;

    let isStale = false;

    (async () => {
      const { defaultDays, minDays, maxDays } = await getFirstPaymentDate(
        afgDealerId,
        afgLenderId,
        contractDateRef.current,
      );

      if (isStale) return;

      const date = addDays(contractDateRef.current ?? getStartOfToday(), defaultDays);

      setValue(LOAN_VARIABLES_FIELDS_NAMES.FIRST_PAYMENT_DATE, date);
      overrideFormHistoryValues({ firstPaymentDate: date });
      setFirstPaymentDaysRange({ default: defaultDays, min: minDays, max: maxDays });
    })();

    return () => {
      isStale = true;
    };
  }, [balloonToggle, afgDealerId, afgLenderId, setValue, overrideFormHistoryValues, setFirstPaymentDaysRange]);
};
